import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';

import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import GetAutoEnrolmentStatusPublicQuery from 'bundles/unified-common/queries/GetAutoEnrolmentStatusPublicQuery.graphql';
import type {
  GetAutoEnrolmentStatusPublicQueryQuery,
  GetAutoEnrolmentStatusPublicQueryQueryVariables,
} from 'bundles/unified-common/queries/__generated__/GetAutoEnrolmentStatusPublicQuery';

export const useAutoenrollmentStatus = () => {
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const isFeatureFlagLoading = useEnterpriseProductFeaturesLoading();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const isExternal = location.query.isExternal === 'true';
  const isOptimizedLearnerFlowSettingEnabled = features?.enableOptimisedEnrollmentSetting && isExternal;

  const { data, error, loading } = useQuery<
    GetAutoEnrolmentStatusPublicQueryQuery,
    GetAutoEnrolmentStatusPublicQueryQueryVariables
  >(GetAutoEnrolmentStatusPublicQuery, {
    variables: {
      organizationId: thirdPartyOrganization?.id || '',
    },
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
    skip: !isExternal || !isOptimizedLearnerFlowSettingEnabled,
  });

  const isLmsLxpAutoEnrollmentEnabled = useMemo(() => {
    if (error) return false;

    const autoEnrollmentEnabled = data?.ThirdPartyOrganizationConfiguration?.isLmsLxpAutoEnrollmentEnabled;
    if (!autoEnrollmentEnabled) return false;

    return !!autoEnrollmentEnabled;
  }, [data, error]);
  const isLoading = loading || isFeatureFlagLoading === undefined || isFeatureFlagLoading;

  return {
    isLmsLxpAutoEnrollmentEnabled,
    isLoading,
  };
};
